import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import {
  classificarRespostaDeSaude,
  decidirEstadoDeSaude,
  normalizarVersaoDoEsquema,
  type MotivoDeAtualizacao,
  type RespostaDeSaude,
} from "../../lib/versao-esquema";
import { FaixaDeInstabilidade } from "./FaixaDeInstabilidade";
import { SistemaEmAtualizacao, type SistemaEmAtualizacaoTextos } from "./SistemaEmAtualizacao";

/**
 * Guarda de versão do esquema (doc: docs/funcionalidades/versao-do-app-e-do-esquema.md).
 *
 * - OTIMISTA: o app renderiza desde o início; a checagem não segura o boot.
 * - DEFINITIVO (banco atrás, função ausente) → página "Sistema em atualização"
 *   no lugar do app, a qualquer momento.
 * - TRANSITÓRIO (503, PGRST002, rede com o navegador online) só conta na 3ª
 *   falha seguida, tentando de novo a cada `intervaloTransitorioMs` (10 s):
 *   * na PARTIDA (ainda sem nenhuma resposta OK) → página;
 *   * com o app MONTADO (já houve OK) → só a faixa "Conexão instável", por
 *     cima do app. Nunca desmonta o app por isso: trocar o children derruba o
 *     QueryClientProvider e o usuário perderia o formulário por um 503 de
 *     segundos (Gate 1 do #822).
 * - Página/faixa abertas: nova tentativa a cada `intervaloMs` (30 s); OK fecha.
 * - Fora disso: checa de novo ao voltar o foco (≥ `focoMinimoMs`) e no `online`.
 * - Sem `esperada` (dev, build fora da imagem, nativo) é transparente e não chama a API.
 *
 * O app injeta `consultar` (rpc) e `aoEntrar` (Sentry); o pacote não conhece nenhum dos dois.
 */
export interface InfoDeAtualizacao {
  /** `pagina` troca o app; `faixa` fica por cima dele. */
  tela: "pagina" | "faixa";
  motivo: MotivoDeAtualizacao;
  esperada: string;
  versaoDoBanco: string | null;
}

export interface GuardaDeVersaoDoEsquemaProps {
  esperada: string | null | undefined;
  consultar: () => PromiseLike<RespostaDeSaude>;
  textos: SistemaEmAtualizacaoTextos;
  /** "Conexão instável — tentando de novo" */
  textoFaixa: string;
  /** Chamado UMA vez por entrada na página ou na faixa (não a cada nova tentativa). */
  aoEntrar?: (info: InfoDeAtualizacao) => void;
  rodape?: string;
  intervaloMs?: number;
  intervaloTransitorioMs?: number;
  focoMinimoMs?: number;
  children: ReactNode;
}

type Tela = "app" | "faixa" | "pagina";

function navegadorOnline(): boolean {
  return typeof navigator === "undefined" || navigator.onLine !== false;
}

export function GuardaDeVersaoDoEsquema({
  esperada: esperadaBruta,
  consultar,
  textos,
  textoFaixa,
  aoEntrar,
  rodape,
  intervaloMs = 30_000,
  intervaloTransitorioMs = 10_000,
  focoMinimoMs = 5 * 60_000,
  children,
}: GuardaDeVersaoDoEsquemaProps) {
  const esperada = normalizarVersaoDoEsquema(esperadaBruta);
  const [tela, setTela] = useState<Tela>("app");
  const [verificando, setVerificando] = useState(false);
  const [proximaEm, setProximaEm] = useState<number | null>(null);
  const [agora, setAgora] = useState(() => Date.now());

  const consultarRef = useRef(consultar);
  consultarRef.current = consultar;
  const aoEntrarRef = useRef(aoEntrar);
  aoEntrarRef.current = aoEntrar;

  const falhas = useRef(0);
  const jaTeveOk = useRef(false);
  const emCurso = useRef(false);
  const ultimaChecagem = useRef(0);
  const telaAtual = useRef<Tela>("app");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const mudarTela = (nova: Tela) => {
    telaAtual.current = nova;
    setTela(nova);
  };

  const checar = useCallback(async () => {
    if (!esperada || emCurso.current) return;
    emCurso.current = true;
    setVerificando(true);
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    let resposta: RespostaDeSaude;
    try {
      resposta = await consultarRef.current();
    } catch {
      // `consultar` que lança (em vez de devolver `{ error }`) é falha de rede.
      resposta = { data: null, error: { code: null, message: "fetch" }, status: 0 };
    }
    ultimaChecagem.current = Date.now();
    const online = navegadorOnline();
    const decisao = decidirEstadoDeSaude(classificarRespostaDeSaude(esperada, resposta), falhas.current, {
      montado: jaTeveOk.current,
      online,
    });
    falhas.current = decisao.falhasTransitorias;
    emCurso.current = false;
    setVerificando(false);

    const agendar = (ms: number) => {
      timer.current = setTimeout(() => void checar(), ms);
    };

    switch (decisao.estado) {
      case "ok":
        jaTeveOk.current = true;
        mudarTela("app");
        setProximaEm(null);
        return;
      case "repetir":
        if (telaAtual.current === "pagina") {
          setProximaEm(Date.now() + intervaloMs); // o relógio da página tenta de novo
        } else if (telaAtual.current === "faixa") {
          agendar(intervaloMs);
        } else if (online) {
          agendar(intervaloTransitorioMs);
        }
        // Offline: nada agendado — o evento `online` chama de novo.
        return;
      case "faixa":
        // Com a página aberta (definitivo antes), não rebaixa para faixa.
        if (telaAtual.current === "pagina") {
          setProximaEm(Date.now() + intervaloMs);
          return;
        }
        if (telaAtual.current !== "faixa") {
          aoEntrarRef.current?.({ tela: "faixa", motivo: decisao.motivo, esperada, versaoDoBanco: null });
          mudarTela("faixa");
        }
        agendar(intervaloMs);
        return;
      case "pagina":
        if (telaAtual.current !== "pagina") {
          aoEntrarRef.current?.({
            tela: "pagina",
            motivo: decisao.motivo,
            esperada,
            versaoDoBanco: decisao.versaoDoBanco,
          });
          mudarTela("pagina");
        }
        setProximaEm(Date.now() + intervaloMs);
        return;
    }
  }, [esperada, intervaloMs, intervaloTransitorioMs]);

  // Na montagem.
  useEffect(() => {
    void checar();
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [checar]);

  // Foco/visibilidade (com intervalo mínimo) e volta da rede.
  useEffect(() => {
    if (!esperada) return;
    const aoFocar = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      if (telaAtual.current !== "app") return; // página e faixa já têm o próprio relógio
      if (Date.now() - ultimaChecagem.current < focoMinimoMs) return;
      void checar();
    };
    const aoFicarOnline = () => {
      if (falhas.current > 0 && telaAtual.current !== "pagina") void checar();
    };
    window.addEventListener("focus", aoFocar);
    window.addEventListener("online", aoFicarOnline);
    document.addEventListener("visibilitychange", aoFocar);
    return () => {
      window.removeEventListener("focus", aoFocar);
      window.removeEventListener("online", aoFicarOnline);
      document.removeEventListener("visibilitychange", aoFocar);
    };
  }, [esperada, focoMinimoMs, checar]);

  // Relógio da contagem regressiva — só com a página aberta.
  useEffect(() => {
    if (tela !== "pagina" || proximaEm === null) return;
    const id = setInterval(() => setAgora(Date.now()), 1_000);
    return () => clearInterval(id);
  }, [tela, proximaEm]);

  useEffect(() => {
    if (tela === "pagina" && proximaEm !== null && !verificando && agora >= proximaEm) {
      void checar();
    }
  }, [agora, proximaEm, verificando, tela, checar]);

  if (tela === "pagina") {
    const segundos = proximaEm === null ? 0 : Math.max(0, Math.ceil((proximaEm - agora) / 1_000));
    return (
      <SistemaEmAtualizacao
        textos={textos}
        segundosParaNovaTentativa={segundos}
        verificando={verificando}
        onTentarAgora={() => void checar()}
        rodape={rodape}
      />
    );
  }
  // A posição do `children` no fragmento é FIXA (índice 1) com ou sem faixa:
  // ligar/desligar a faixa não pode remontar o app.
  return (
    <>
      {tela === "faixa" ? <FaixaDeInstabilidade texto={textoFaixa} /> : null}
      {children}
    </>
  );
}
